import {Component, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {MatSelectChange} from '@angular/material/select';
import {validateInteger} from '../../../commons/validators';
import {downloadBlob, errorCode, isDefined} from '../../../commons/commons';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {first} from 'rxjs/operators';
import {ConfigurationService} from '../../../shared/configuration.service';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {DATE_FORMATS} from '../../../i18n/i18n-defaults';
import {SmagApiService} from '../../../api/smag-api.service';
import {DialogData} from '../ec-measures.component';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {I18NService} from '../../../i18n/i18n.service';
import {ErrorCode, GseErrorCode} from '../../../api/errors';
import {HttpErrorResponse} from '@angular/common/http';
import {BehaviorSubject, Subject} from 'rxjs';
import {SpinnerData} from 'src/app/shared/spinner/spinner.component';


const MAX_FILE_MB = 2;
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

interface Template {
    name: string;
    file: File;
}

export interface TimeRange {
    fromTime: number; // epoch in milliseconds
    toTime: number; // epoch in milliseconds
    includesNow: boolean; // whether this range is something like "Last ... or ... so far" ("hourly" ranges do *not* include now by definition)
}

export interface DateBoundaries {
    minDateTime: Moment; // inclusive
    maxDateTime: Moment; // inclusive
    delta?: number;
    deltaUoT?: string;
}

const DIALOG_WIDTH = 700;

@Component({
    selector: 'app-measures-upload',
    templateUrl: './measures-upload.component.html',
    styleUrls: ['./measures-upload.component.scss'],
    providers: [
        {provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS}
    ]
})
export class MeasuresUploadComponent implements OnInit {

    @Input() resource: DialogData;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;

    form: FormGroup;
    customTimeRangeForm: FormGroup;
    currentStep: number;
    STEPS: string[];
    TIME_RANGES: { [key: string]: number };
    XLSX_MIME_TYPE: string = XLSX_MIME_TYPE;
    MAX_FILE_MB: number = MAX_FILE_MB;
    templateChosen: boolean;
    templateDownloaded: boolean;
    fileUploaded;
    template: Template;
    private timeZone: string;
    private boundaries: DateBoundaries;
    private selectedMinutesInterval: number;


    constructor(private readonly dialogService: DialogService,
                private readonly configurationService: ConfigurationService,
                private readonly apiService: SmagApiService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private fb: FormBuilder) {

        this.STEPS = [
            'Choose Period',
            'Choose time unit',
            'Download template',
            'Upload file'
        ];
        this.TIME_RANGES = {
            Hourly: 60,
            'Quarterly hour': 15
        };
        this.currentStep = 1;
        this.templateChosen = false;
        this.templateDownloaded = false;
        this.template = {name: null, file: null};
        this.customTimeRangeForm = this.fb.group({
            startDate: new FormControl(null, [Validators.required]),
            startHour: new FormControl(null, Validators.compose([Validators.required, validateInteger, Validators.min(0), Validators.max(23)])),
            endDate: new FormControl(null, [Validators.required]),
            endHour: new FormControl(null, Validators.compose([Validators.required, validateInteger, Validators.min(0), Validators.max(23)]))
        }, {
            validators: [ctrl => this.validateCustomTimeRange(ctrl)]
        });
        this.form = this.fb.group({
            timeRange: new FormControl(null, [Validators.required])
        });
        this.timeZone = null;
        this.selectedMinutesInterval = null;
        this.fileUploaded = false;
    }

    ngOnInit(): void {
        this.configurationService.timeConfig.pipe(
            first()
        ).subscribe(timeCfg => {
            this.init(timeCfg.time_zone);
        });
    }


    private init(timeZone: string) {
        this.timeZone = timeZone;
        const delta = 2;
        const deltaUoT = 'years';
        const now = moment.tz(timeZone);
        const min = now.clone().startOf(deltaUoT).subtract(delta, deltaUoT);
        // date boundaries
        this.boundaries = {
            minDateTime: min,
            maxDateTime: now,
            delta,
            deltaUoT
        };

    }

    get maxDate(): Moment {
        return this.boundaries.maxDateTime.clone().startOf('day');
    }

    get minDate(): Moment {
        return this.boundaries.minDateTime.clone().startOf('day');
    }

    get startMinDate(): Moment {
        return this.customTimeRangeForm.controls.endDate.value ?
            this.customTimeRangeForm.controls.endDate.value.clone().startOf('day').subtract(this.boundaries.delta, this.boundaries.deltaUoT) :
            this.minDate;
    }

    get startMaxDate(): Moment {
        return this.customTimeRangeForm.controls.endDate.value ?
            this.customTimeRangeForm.controls.endDate.value :
            this.boundaries.maxDateTime.clone().endOf('day');
    }

    get endMinDate(): Moment {
        return this.customTimeRangeForm.controls.startDate.value ?
            this.customTimeRangeForm.controls.startDate.value :
            this.minDate;
    }

    get endMaxDate(): Moment {
        return this.customTimeRangeForm.controls.startDate.value ?
            moment.min([this.maxDate, this.customTimeRangeForm.controls.startDate.value.clone()
                .startOf('day').add(this.boundaries.delta, this.boundaries.deltaUoT)]) :
            this.maxDate;
    }

    get isFirstPageValid(): boolean {
        return this.customTimeRangeForm.valid;
    }

    get isSecondPageValid(): boolean {
        return this.form.get('timeRange').valid;
    }

    get isThirdPageValid(): boolean {
        return true;
    }


    isNextEnabled(step: number) {
        switch (step) {
            case 1:
                return this.isFirstPageValid;
            case 2:
                return this.isSecondPageValid;
            case 3:
                return this.isThirdPageValid;
            default:
                return false;
        }
    }

    nextStep() {
        this.currentStep++;
    }

    prevStep() {
        this.currentStep--;
    }

    mkTimeRange(): TimeRange {
        const start = (this.customTimeRangeForm.controls.startDate.value as Moment).clone()
            .hours(this.customTimeRangeForm.controls.startHour.value);
        const end = (this.customTimeRangeForm.controls.endDate.value as Moment).clone()
            .hours(this.customTimeRangeForm.controls.endHour.value);
        const startValue = start.valueOf();
        const endValue = end.valueOf();
        const now = moment.tz(this.timeZone).valueOf();
        return {
            fromTime: startValue,
            toTime: endValue,
            includesNow: now >= startValue && now <= endValue
        } as TimeRange;
    }


    sendIt() {
        this.fileUploaded = true;
        this.nextStep();
        const timeRange = this.mkTimeRange();
        const spinner = this.openSpinner('We\'re uploading your measures.\n You\'ll see them shortly.');
        this.apiService.uploadMeasuresTemplateExcel(this.resource.resourceId,
            timeRange.fromTime,
            timeRange.toTime,
            this.selectedMinutesInterval,
            this.resource.type,
            {content: this.template.file, filename: this.template.name})
            .then(() => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('File correctly uploaded: measures will arrive shortly'),
                });
            })
            .catch(error => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('Error during document upload'),
                    abort: true
                });
                if (errorCode(error) === ErrorCode.INVALID_MEASURE) {
                    this.msgBoxService.error(this.i18n.translate('File contains invalid measures'));
                } else if (errorCode(error) === ErrorCode.INVALID_NEGATIVE_MEASURE) {
                    this.msgBoxService.error(this.i18n.translate('File contains negative values'));
                } else if (errorCode(error) === ErrorCode.DATES_NOT_MATCH_TIME_INTERVAL) {
                    this.msgBoxService.error(this.i18n.translate('Dates provided inside the file do not match the specified interval'));
                } else if (errorCode(error) === ErrorCode.UNSUPPORTED_TIME_INTERVAL) {
                    this.msgBoxService.error(this.i18n.translate('You specified an invalid time interval'));
                } else if (errorCode(error) === ErrorCode.INVALID_TIME_RANGE) {
                    this.msgBoxService.error(this.i18n.translate('The file provided contains an invalid time range'));
                } else if (errorCode(error) === ErrorCode.INVALID_FILE_TYPE) {
                    this.msgBoxService.error(this.i18n.translate('Invalid template file for chosen resource'));
                } else if (errorCode(error) === GseErrorCode.INVALID_GSE_FILE) {
                    this.msgBoxService.error(this.i18n.translate('Invalid GSE file'));
                } else if (error instanceof HttpErrorResponse && (error as HttpErrorResponse).status === 413) {
                    this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', this.template.name, MAX_FILE_MB));
                } else {
                    this.msgBoxService.error(this.i18n.translate('An unexpected error occurred while uploading the file'));
                }
            });

    }

    onSelectTimeFilter($event: MatSelectChange) {

        this.selectedMinutesInterval = $event.value;

    }

    onClickDownloadTemplate() {

        const timeRange = this.mkTimeRange();
        this.apiService.downloadMeasuresTemplateExcel(this.resource.resourceId,
            timeRange.fromTime,
            timeRange.toTime,
            this.selectedMinutesInterval,
            this.resource.type)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
                this.templateDownloaded = true;
            }, error => {
                this.msgBoxService.error(this.i18n.translate('There was an error while downloading the file'));
            });

    }

    onClickUploadTemplate($event: Event) {
        const tg = $event.target as HTMLInputElement;
        if (tg.files && tg.files.length) {
            const file: File = tg.files[0];
            if (this.validateFile(file)) {
                this.template = {name: file.name, file};
                this.templateChosen = true;
            }

        }
    }

    private validateFile(file: File): boolean {
        if (file.size > MAX_FILE_MB * 1048576) {
            this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
            return false;
        }
        if (file.type !== XLSX_MIME_TYPE) {
            this.msgBoxService.error(this.i18n.translate('File {0} is not an XLSX file', file.name));
            return false;
        }
        return true;
    }

    onClickTrashTemplate(fileSelect: HTMLInputElement) {
        fileSelect.value = null; // allows file re-selection
        this.template = {name: null, file: null};
        this.templateChosen = false;
    }

    get startMaxHour(): number {
        const now = moment.tz(this.timeZone);
        return this.customTimeRangeForm.controls.startDate.value &&
        this.customTimeRangeForm.controls.startDate.value.year() === now.year() &&
        this.customTimeRangeForm.controls.startDate.value.month() === now.month() &&
        this.customTimeRangeForm.controls.startDate.value.dayOfYear() === now.dayOfYear() ?
            this.boundaries.maxDateTime.hour() : 23;
    }

    get endMaxHour(): number {
        const now = moment.tz(this.timeZone);
        return this.customTimeRangeForm.controls.endDate.value &&
        this.customTimeRangeForm.controls.endDate.value.year() === now.year() &&
        this.customTimeRangeForm.controls.endDate.value.month() === now.month() &&
        this.customTimeRangeForm.controls.endDate.value.dayOfYear() === now.dayOfYear() ?
            this.boundaries.maxDateTime.hour() : 23;
    }

    get minHour(): number {
        return 0;
    }

    private startHourInRange(startHour: number): boolean {
        return startHour >= this.minHour && startHour <= this.startMaxHour;
    }

    private endHourInRange(endHour: number): boolean {
        return endHour >= this.minHour && endHour <= this.endMaxHour;
    }


    private validateCustomTimeRange(ctrl: AbstractControl): ValidationErrors | null {
        const errs: ValidationErrors = {};
        const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
        if (ctrls.startDate.value && ctrls.endDate.value && isDefined(ctrls.startHour.value) && isDefined(ctrls.endHour.value)) {
            if (!fromBeforeTo(ctrls.startDate.value, ctrls.startHour.value, ctrls.endDate.value, ctrls.endHour.value)) {
                errs.fromdateAfterTodate = true;
            }
            // check consistency of dates and hours
            // we cannot entirely rely on min/max of <input>, as one could still enter a valid hour and *then* change the date
            if (!this.startHourInRange(ctrls.startHour.value)) {
                errs.startHourOutOfRange = true;
            }
            if (!this.endHourInRange(ctrls.endHour.value)) {
                errs.endHourOutOfRange = true;
            }
        }
        return Object.values(errs).every(err => !err) ? null : errs;
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DIALOG_WIDTH,
            hideCloseButton: true
        });
    }


}

function mkTime(date: Moment, hour: number): Moment {
    return date.clone().hour(hour);
}

function fromBeforeTo(startDate: Moment, startHour: number, endDate: Moment, endHour: number): boolean {
    const startDateHour: Moment = mkTime(startDate, startHour);
    const endDateHour: Moment = mkTime(endDate, endHour);
    return startDateHour.isBefore(endDateHour);
}
